import get from 'lodash/get';

import CheckIcon from '@mui/icons-material/Check';
import Banner from '@zenbusiness/zui/Banner';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Card from '@zenbusiness/zui/Card';
import Stack from '@zenbusiness/zui/Stack';
import { superstyled } from '@zenbusiness/zui/styles';
import Tab from '@zenbusiness/zui/Tab';
import Tabs from '@zenbusiness/zui/Tabs';
import Text from '@zenbusiness/zui/Text';

export const BuyBoxWrapper = superstyled({
  name: 'ZuiBuyBoxWrapper',
  slot: 'Root',
  tag: Box,
  styles: ({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
      position: 'relative',
      zIndex: 1,
    },
  }),
});

export const BuyBoxContainer = superstyled({
  name: 'ZuiBuyBoxContainer',
  slot: 'Root',
  tag: Stack,
  propsNeeded: ['elevated'],
  styles: ({ theme, elevated }) => ({
    gap: theme.spacing(2),

    ...(elevated && {
      borderRadius: theme.shape.rounded['2xl'],
      boxShadow: theme.shadows[3],
    }),

    [theme.breakpoints.up('lg')]: {
      position: 'absolute',
      top: theme.spacing(3),
      width: '100%',
    },
  }),
});

export const TabsContainer = superstyled({
  name: 'ZuiTabsContainer',
  slot: 'Root',
  tag: Tabs,
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.background.grey.dark,
    borderRadius: theme.shape.rounded.lg,
    alignItems: 'center',
    height: '4rem',

    '& .MuiTabs-flexContainer': {
      width: '100%',
      gap: 0,
    },

    '& .MuiTabs-indicator': {
      display: 'none',
    },
  }),
});

export const TabButton = superstyled({
  name: 'ZuiTabButton',
  slot: 'Root',
  tag: Tab,
  styles: ({ theme, selected }) => ({
    flex: 1,
    maxWidth: 'unset',
    height: '4rem',
    padding: 0,
    borderRadius: theme.shape.rounded.lg,
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.background.grey.dark,
  }),
});

export const BuyBox = superstyled({
  name: 'ZuiBuyBox',
  slot: 'Root',
  tag: Card,
  propsAdded: {
    color: 'paper',
  },
  styles: ({ theme, note, color }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: 0,
    backgroundColor: color ? get(theme, color) : 'transparent',
    borderColor: note ? 'transparent' : theme.palette.background.grey.dark,
  }),
});

export const BuyBoxContent = superstyled({
  name: 'ZuiBuyBoxContent',
  slot: 'Root',
  tag: Box,
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.background.grey.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    boxShadow: theme.shadows[3],
    padding: theme.spacing(1.5),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(2),
    },
  }),
});

export const ButtonLinkContainer = superstyled({
  tag: Stack,
  name: 'BuyBox',
  slot: 'ButtonLinkContainer',
  styles: ({ theme }) => ({
    alignItems: 'center',

    [theme.breakpoints.up('lg')]: {
      alignItems: 'end',
    },
  }),
});

export const ButtonLink = superstyled({
  tag: Button,
  name: 'BuyBox',
  slot: 'ButtonLink',
  styles: () => ({
    width: 'fit-content',

    '&:hover, &:focus': {
      backgroundColor: 'transparent',
    },

    '& span': {
      margin: 0,
    },
  }),
});

export const Badge = superstyled({
  name: 'ZuiBuyBoxBadge',
  slot: 'Root',
  tag: Text,
  propsAdded: {
    size: 'sm',
  },
  styles: () => ({
    whiteSpace: 'nowrap',
  }),
});

export const PriceContainer = superstyled({
  name: 'ZuiBuyBoxPriceContainer',
  slot: 'Root',
  tag: Stack,
  styles: ({ theme }) => ({
    alignItems: 'end',
    gap: theme.spacing(0.5),
  }),
});

export const BuyBoxText = superstyled({
  name: 'ZuiSubheader',
  slot: 'Root',
  tag: Text,
  propsAdded: {
    size: 'lg',
  },
});

export const FeeText = superstyled({
  name: 'ZuiBuyBoxFee',
  slot: 'Root',
  tag: Text,
  propsAdded: {
    size: 'xs',
    color: 'tertiary',
    textAlign: 'right',
  },
  styles: ({ theme }) => ({
    display: 'none',

    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  }),
});

export const StyledCouponBanner = superstyled({
  tag: Banner,
  name: 'BuyBox',
  slot: 'StyledCouponBanner',
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.background.green.light,
    color: theme.palette.text.primary,

    '& .MuiStack-root': {
      gap: '0 !important',
    },
  }),
});

export const StrikethroughPrice = superstyled({
  name: 'ZuiBuyBoxStrikethroughPrice',
  slot: 'Root',
  tag: Text,
  propsAdded: {
    size: 'sm',
    color: 'primary',
  },
  styles: () => ({
    textDecoration: 'line-through',
  }),
});

export const DiscountPriceContainer = superstyled({
  name: 'ZuiBuyBoxDiscountPriceContainer',
  slot: 'Root',
  tag: Stack,
  styles: ({ theme }) => ({
    alignItems: 'flex-end',
    gap: theme.spacing(0),

    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
    },
  }),
});

export const FeatureItem = superstyled({
  name: 'ZuiFeatureItem',
  slot: 'Root',
  tag: Stack,
  styles: ({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),

    [theme.breakpoints.up('lg')]: {
      width: '100%',
    },
  }),
});

export const SmallCopyText = superstyled({
  name: 'ZuiSmallCopyText',
  slot: 'Root',
  tag: Text,
  propsAdded: {
    color: 'tertiary',
    size: 'sm',
  },
});

export const PackageNoteLabel = superstyled({
  tag: Text,
  name: 'BuyBox',
  slot: 'PackageNoteLabel',
  styles: {
    alignContent: 'center',
    textAlign: 'center',
    height: '1.75rem',
  },
});

export const ComparePackagesButton = superstyled({
  tag: ButtonLink,
  name: 'BuyBox',
  slot: 'ComparePackagesButton',
  styles: {
    padding: 0,
  },
});

export const CouponMessageText = superstyled({
  tag: Text,
  name: 'BuyBox',
  slot: 'CouponMessageText',
  styles: {
    textAlign: 'right',
  },
});

export const FeatureCheckIcon = superstyled({
  tag: CheckIcon,
  name: 'BuyBox',
  slot: 'FeatureCheckIcon',
  styles: {
    width: '1.125rem',
    height: '1.125rem',
  },
});

export const TooltipIconWrapper = superstyled({
  tag: Box,
  name: 'BuyBox',
  slot: 'TooltipIconWrapper',
  styles: {
    pointerEvents: 'auto',
  },
});

export const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      width: '13rem',
      borderRadius: '0.25rem',
      padding: '0.5rem',
    },
  },
};
